import { Ship, HardHat, FileCheck2, Stamp, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, ChapterHeading } from "@/components/Reveal";
import { WHATSAPP_QUOTE_URL } from "@/i18n";

const IMG_CHANDLER =
  "https://images.pexels.com/photos/16482922/pexels-photo-16482922/free-photo-of-ropes-on-vessel.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_GEAR =
  "https://images.pexels.com/photos/30169171/pexels-photo-30169171/free-photo-of-maritime-worker-in-orange-uniform-on-ship-deck.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_CREW =
  "https://images.pexels.com/photos/37171408/pexels-photo-37171408/free-photo-of-crew-members-on-a-ship-deck-in-indonesia.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const IMG_VISA =
  "https://images.pexels.com/photos/4922356/pexels-photo-4922356.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Services = () => {
  const { t } = useLanguage();

  const icons = [Ship, HardHat, FileCheck2, Stamp];
  const images = [IMG_CHANDLER, IMG_GEAR, IMG_CREW, IMG_VISA];
  const slugs = ["ship-chandler", "working-gear-ppe", "crew-documentation", "visa-embassy"];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-mist py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-20">
        <ChapterHeading number={t.services.number} title={t.services.chapter} />

        <div className="mt-12 grid items-start gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <h2
              data-testid="services-title"
              className="font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl lg:text-5xl"
            >
              {t.services.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <p data-testid="services-desc" className="text-sm leading-relaxed text-ink/65 sm:text-base">
              {t.services.desc}
            </p>
            <p data-testid="services-desc-2" className="mt-4 text-sm leading-relaxed text-ink/65 sm:text-base">
              {t.services.desc2}
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {t.services.items.map((item, i) => {
            const Icon = icons[i];
            return (
              <Reveal key={item.title} delay={0.08 * i}>
                <Link
                  data-testid={`service-card-${i}`}
                  to={`/services/${slugs[i]}`}
                  className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border border-navy/10 bg-white transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_56px_rgba(0,43,91,0.16)]"
                >
                  <div className="relative h-52 overflow-hidden sm:h-60">
                    <img
                      src={images[i]}
                      alt={item.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div
                      className="absolute inset-0 bg-gradient-to-t from-navy-950/70 via-navy-900/20 to-transparent"
                      aria-hidden="true"
                    />
                    <span className="absolute left-6 top-6 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-gold backdrop-blur-md transition-colors duration-500 group-hover:bg-gold group-hover:text-navy-950">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span
                      className="absolute bottom-3 right-6 font-display text-4xl font-bold italic text-white/25"
                      aria-hidden="true"
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <h3 className="font-display text-xl font-bold text-navy sm:text-2xl">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-gold-dark">
                      {item.subtitle}
                    </p>
                    <p className="mt-4 text-sm leading-relaxed text-ink/65">{item.desc}</p>
                    <span
                      data-testid={`service-learn-more-${i}`}
                      className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-navy transition-colors duration-300 group-hover:text-gold-dark"
                    >
                      {t.services.learnMore}
                      <ArrowUpRight
                        className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        aria-hidden="true"
                      />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-12 flex justify-center">
            <a
              data-testid="services-quote-button"
              href={WHATSAPP_QUOTE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 rounded-full border-2 border-navy px-8 py-3.5 text-sm font-bold text-navy transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-navy hover:text-white"
            >
              {t.services.cta}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Services;
